import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { LoanType } from '@/types';
import type {
  QuestionnaireTemplate,
  QuestionnaireForm,
  QuestionnaireResponse,
  MergedQuestionnaireForm,
  TemplateSection,
  TemplateField,
} from '@/types/questionnaire';
import { getQuestionnaireForLoanType } from '@/config/questionnaireConfig';

// ============================================================================
// COLLECTION REFERENCES
// ============================================================================

const templatesCollection = collection(db, 'questionnaire_templates');
const formsCollection = collection(db, 'questionnaire_forms');
const responsesCollection = collection(db, 'questionnaire_responses');

// ============================================================================
// TIMESTAMP HELPERS
// ============================================================================

/**
 * Convert Firestore timestamp to Date
 */
const convertTimestamp = (timestamp: Date | Timestamp | undefined): Date => {
  if (timestamp instanceof Date) return timestamp;
  if (timestamp && typeof timestamp === 'object' && 'toDate' in timestamp) {
    return timestamp.toDate();
  }
  return new Date();
};

/**
 * Convert Date to Firestore Timestamp
 */
const toTimestamp = (date: Date | undefined): Timestamp => {
  if (date instanceof Date && !isNaN(date.getTime())) {
    return Timestamp.fromDate(date);
  }
  return Timestamp.now();
};

// ============================================================================
// TEMPLATE OPERATIONS
// ============================================================================

/**
 * Get base template for a loan type
 */
export const getBaseTemplate = async (loanType: LoanType): Promise<QuestionnaireTemplate | null> => {
  try {
    const docRef = doc(templatesCollection, loanType);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      console.log(`No base template found for ${loanType}`);
      return null;
    }
    
    const data = docSnap.data();
    return {
      ...data,
      id: docSnap.id,
      createdAt: convertTimestamp(data.createdAt),
      updatedAt: convertTimestamp(data.updatedAt),
    } as QuestionnaireTemplate;
  } catch (error) {
    console.error(`Error fetching base template for ${loanType}:`, error);
    return null;
  }
};

/**
 * Get all active templates
 */
export const getAllTemplates = async (): Promise<QuestionnaireTemplate[]> => {
  try {
    const q = query(templatesCollection, where('isActive', '==', true));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(docSnap => {
      const data = docSnap.data();
      return {
        ...data,
        id: docSnap.id,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
      } as QuestionnaireTemplate;
    });
  } catch (error) {
    console.error('Error fetching templates:', error);
    return [];
  }
};

/**
 * Save or update a template
 */
export const saveTemplate = async (template: QuestionnaireTemplate): Promise<void> => {
  try {
    const docRef = doc(templatesCollection, template.id);
    await setDoc(docRef, {
      ...template,
      updatedAt: Timestamp.now(),
    });
    console.log(`Template ${template.id} saved successfully`);
  } catch (error) {
    console.error(`Error saving template ${template.id}:`, error);
    throw error;
  }
};

// ============================================================================
// FORM OPERATIONS (LENDER-SPECIFIC)
// ============================================================================

/**
 * Get lender-specific form
 * Only returns published forms that are active
 */
export const getLenderForm = async (
  loanType: LoanType,
  lenderId: string
): Promise<QuestionnaireForm | null> => {
  try {
    const formId = `${loanType}_${lenderId}`;
    const docRef = doc(formsCollection, formId);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      console.log(`No lender form found for ${formId}`);
      return null;
    }
    
    const data = docSnap.data();
    
    // Only return published and active forms for leads
    if (data.status !== 'published' || !data.isActive) {
      console.log(`Lender form ${formId} exists but is not published/active (status: ${data.status}, active: ${data.isActive})`);
      return null;
    }
    
    return {
      ...data,
      id: docSnap.id,
      createdAt: convertTimestamp(data.createdAt),
      updatedAt: convertTimestamp(data.updatedAt),
    } as QuestionnaireForm;
  } catch (error) {
    console.error(`Error fetching lender form for ${loanType}_${lenderId}:`, error);
    return null;
  }
};

/**
 * Get all forms for a loan type
 */
export const getFormsForLoanType = async (loanType: LoanType): Promise<QuestionnaireForm[]> => {
  try {
    const q = query(
      formsCollection,
      where('loanType', '==', loanType),
      where('isActive', '==', true)
    );
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(docSnap => {
      const data = docSnap.data();
      return {
        ...data,
        id: docSnap.id,
        createdAt: convertTimestamp(data.createdAt),
        updatedAt: convertTimestamp(data.updatedAt),
      } as QuestionnaireForm;
    });
  } catch (error) {
    console.error(`Error fetching forms for ${loanType}:`, error);
    return [];
  }
};

/**
 * Save lender-specific form
 */
export const saveLenderForm = async (form: QuestionnaireForm): Promise<void> => {
  try {
    const docRef = doc(formsCollection, form.id);
    await setDoc(docRef, {
      ...form,
      updatedAt: Timestamp.now(),
    });
    console.log(`Lender form ${form.id} saved successfully`);
  } catch (error) {
    console.error(`Error saving lender form ${form.id}:`, error);
    throw error;
  }
};

// ============================================================================
// MERGE LOGIC
// ============================================================================

/**
 * Merge lender form customizations with base template
 */
export const mergeWithBaseTemplate = (
  baseTemplate: QuestionnaireTemplate,
  lenderForm?: QuestionnaireForm | null
): MergedQuestionnaireForm => {
  // If no lender form, just return the base template as merged form
  if (!lenderForm) {
    return {
      id: baseTemplate.id,
      loanType: baseTemplate.loanType,
      name: baseTemplate.name,
      description: baseTemplate.description,
      sections: baseTemplate.sections,
      requiredDocuments: baseTemplate.requiredDocuments,
      templateId: baseTemplate.id,
      version: baseTemplate.version,
      source: 'firebase',
    };
  }
  
  // Clone sections to avoid mutating original
  let mergedSections = JSON.parse(JSON.stringify(baseTemplate.sections)) as TemplateSection[];
  
  // Apply section overrides
  if (lenderForm.sectionOverrides) {
    for (const override of lenderForm.sectionOverrides) {
      if (override.action === 'remove') {
        mergedSections = mergedSections.filter(s => s.id !== override.sectionId);
      } else if (override.action === 'add' && override.section) {
        mergedSections.push(override.section as TemplateSection);
      } else if (override.action === 'modify' && override.section) {
        const index = mergedSections.findIndex(s => s.id === override.sectionId);
        if (index !== -1) {
          mergedSections[index] = { ...mergedSections[index], ...override.section };
        }
      }
    }
  }
  
  // Apply field overrides
  if (lenderForm.fieldOverrides) {
    for (const override of lenderForm.fieldOverrides) {
      const section = mergedSections.find(s => s.id === override.sectionId);
      if (!section) continue;
      
      if (override.action === 'remove') {
        section.fields = section.fields.filter(f => f.id !== override.fieldId);
      } else if (override.action === 'add' && override.field) {
        section.fields.push(override.field as TemplateField);
      } else if (override.action === 'modify' && override.field) {
        const fieldIndex = section.fields.findIndex(f => f.id === override.fieldId);
        if (fieldIndex !== -1) {
          section.fields[fieldIndex] = { ...section.fields[fieldIndex], ...override.field };
        }
      }
    }
  }
  
  // Sort sections by order
  mergedSections.sort((a, b) => a.order - b.order);
  
  // Sort fields within each section by order
  mergedSections.forEach(section => {
    section.fields.sort((a, b) => (a.order || 0) - (b.order || 0));
  });
  
  // Merge required documents
  const requiredDocs = [
    ...baseTemplate.requiredDocuments,
    ...(lenderForm.additionalDocuments || []),
  ];
  
  return {
    id: lenderForm.id,
    loanType: lenderForm.loanType,
    lenderId: lenderForm.lenderId,
    lenderName: lenderForm.lenderName,
    name: lenderForm.name,
    description: lenderForm.description,
    sections: mergedSections,
    requiredDocuments: [...new Set(requiredDocs)], // Remove duplicates
    templateId: baseTemplate.id,
    formId: lenderForm.id,
    version: lenderForm.version,
    source: 'firebase',
  };
};

// ============================================================================
// MAIN FORM FETCHING FUNCTION
// ============================================================================

/**
 * Get questionnaire form for a loan type and optional lender
 * Implements fallback logic: Firebase form -> Firebase template -> Static config
 */
export const getQuestionnaireForm = async (
  loanType: LoanType,
  lenderId?: string
): Promise<MergedQuestionnaireForm> => {
  try {
    // Step 1: Get base template from Firebase
    const baseTemplate = await getBaseTemplate(loanType);
    
    if (!baseTemplate) {
      // Fallback to static config
      console.log(`Falling back to static config for ${loanType}`);
      return convertStaticToMergedForm(loanType);
    }
    
    // Step 2: If lender specified, try to get lender-specific form
    if (lenderId) {
      const lenderForm = await getLenderForm(loanType, lenderId);
      if (lenderForm) {
        console.log(`Using lender form: ${lenderForm.id}`);
        return mergeWithBaseTemplate(baseTemplate, lenderForm);
      }
    }
    
    // Step 3: Return base template as merged form
    console.log(`Using base template: ${baseTemplate.id}`);
    return mergeWithBaseTemplate(baseTemplate);
    
  } catch (error) {
    console.error('Error fetching questionnaire form:', error);
    // Fallback to static config on any error
    console.log(`Error occurred, falling back to static config for ${loanType}`);
    return convertStaticToMergedForm(loanType);
  }
};

/**
 * Convert static config to MergedQuestionnaireForm format
 */
const convertStaticToMergedForm = (loanType: LoanType): MergedQuestionnaireForm => {
  const staticConfig = getQuestionnaireForLoanType(loanType);
  
  if (!staticConfig) {
    // Return empty form if no static config
    return {
      id: loanType,
      loanType,
      name: loanType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      sections: [],
      requiredDocuments: [],
      templateId: loanType,
      version: 1,
      source: 'static',
    };
  }
  
  // Convert static sections to TemplateSection format
  const sections: TemplateSection[] = staticConfig.sections.map((section, index) => ({
    id: section.id,
    title: section.title,
    description: section.description,
    iconName: section.icon?.name || 'FileCheck',
    order: index,
    fields: section.fields.map((field, fieldIndex) => ({
      id: field.id,
      type: field.type as TemplateField['type'],
      label: field.label,
      placeholder: field.placeholder,
      options: field.options,
      validation: field.validation,
      order: fieldIndex,
      // Note: conditional functions cannot be serialized
      // They are handled separately in the component
    })),
    // Note: section conditional functions cannot be serialized
  }));
  
  return {
    id: loanType,
    loanType,
    name: loanType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    sections,
    requiredDocuments: staticConfig.requiredDocuments,
    templateId: loanType,
    version: 1,
    source: 'static',
  };
};

// ============================================================================
// RESPONSE OPERATIONS
// ============================================================================

/**
 * Get questionnaire response for a lead
 */
export const getQuestionnaireResponse = async (
  leadId: string
): Promise<QuestionnaireResponse | null> => {
  try {
    const q = query(responsesCollection, where('leadId', '==', leadId));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      return null;
    }
    
    // Get the most recent response
    const docSnap = querySnapshot.docs[0];
    const data = docSnap.data();
    
    return {
      ...data,
      id: docSnap.id,
      createdAt: convertTimestamp(data.createdAt),
      updatedAt: convertTimestamp(data.updatedAt),
      lastSavedAt: convertTimestamp(data.lastSavedAt),
      submittedAt: data.submittedAt ? convertTimestamp(data.submittedAt) : undefined,
    } as QuestionnaireResponse;
  } catch (error) {
    console.error(`Error fetching response for lead ${leadId}:`, error);
    return null;
  }
};

/**
 * Get questionnaire form for a lead, considering version locking
 * If lead already has a response with a locked version, use the stored snapshot
 * Otherwise, fetch the latest form from Firebase
 */
export const getQuestionnaireFormForLead = async (
  leadId: string,
  loanType: LoanType,
  lenderId?: string
): Promise<{ form: MergedQuestionnaireForm; existingResponse: QuestionnaireResponse | null; isVersionLocked: boolean }> => {
  try {
    // Check if lead already has a response with version lock
    const existingResponse = await getQuestionnaireResponse(leadId);
    
    if (existingResponse && existingResponse.isVersionLocked && existingResponse.formSnapshot) {
      // Use the stored form snapshot - this ensures form doesn't change after submission
      console.log(`Using version-locked form snapshot for lead ${leadId}`);
      return {
        form: {
          id: existingResponse.formId,
          loanType: existingResponse.loanType,
          lenderId: existingResponse.lenderId,
          name: existingResponse.formSnapshot.name,
          description: existingResponse.formSnapshot.description,
          sections: existingResponse.formSnapshot.sections,
          requiredDocuments: [],
          templateId: existingResponse.templateId,
          formId: existingResponse.formId,
          version: existingResponse.formVersion,
          source: 'firebase',
        },
        existingResponse,
        isVersionLocked: true,
      };
    }
    
    // Fetch latest form from Firebase
    const form = await getQuestionnaireForm(loanType, lenderId);
    
    return {
      form,
      existingResponse,
      isVersionLocked: false,
    };
  } catch (error) {
    console.error(`Error fetching form for lead ${leadId}:`, error);
    // Fallback to standard form fetch
    const form = await getQuestionnaireForm(loanType, lenderId);
    return {
      form,
      existingResponse: null,
      isVersionLocked: false,
    };
  }
};

/**
 * Save questionnaire response with version locking
 * Once a response is created, it locks the form version so future form updates
 * won't affect this lead's questionnaire
 */
export const saveQuestionnaireResponse = async (
  response: Omit<QuestionnaireResponse, 'id' | 'createdAt' | 'updatedAt'>,
  formSnapshot?: { sections: TemplateSection[]; name: string; description?: string }
): Promise<string> => {
  try {
    // Check if response already exists for this lead
    const existing = await getQuestionnaireResponse(response.leadId);
    
    const now = Timestamp.now();
    
    if (existing) {
      // Update existing response BUT keep the original version info (version locked)
      const docRef = doc(responsesCollection, existing.id);
      await updateDoc(docRef, {
        data: response.data,
        currentSectionId: response.currentSectionId,
        completedSections: response.completedSections,
        progress: response.progress,
        isComplete: response.isComplete,
        validationErrors: response.validationErrors,
        updatedAt: now,
        lastSavedAt: now,
        // Note: We do NOT update formVersion, templateVersion, or formSnapshot
        // Those are locked from the first save
      });
      console.log(`Updated response ${existing.id} for lead ${response.leadId} (version locked: ${existing.isVersionLocked})`);
      return existing.id;
    } else {
      // Create new response with version lock
      const docRef = doc(responsesCollection);
      await setDoc(docRef, {
        ...response,
        id: docRef.id,
        createdAt: now,
        updatedAt: now,
        lastSavedAt: now,
        // Lock the version from first save
        isVersionLocked: true,
        formSnapshot: formSnapshot || null,
      });
      console.log(`Created response ${docRef.id} for lead ${response.leadId} with version lock`);
      return docRef.id;
    }
  } catch (error) {
    console.error(`Error saving response for lead ${response.leadId}:`, error);
    throw error;
  }
};

/**
 * Update questionnaire response data
 */
export const updateQuestionnaireResponseData = async (
  leadId: string,
  data: Record<string, unknown>,
  currentSectionId?: string,
  completedSections?: string[],
  progress?: number
): Promise<void> => {
  try {
    const existing = await getQuestionnaireResponse(leadId);
    
    if (!existing) {
      console.warn(`No response found for lead ${leadId}`);
      return;
    }
    
    const docRef = doc(responsesCollection, existing.id);
    const updateData: Record<string, unknown> = {
      data,
      updatedAt: Timestamp.now(),
      lastSavedAt: Timestamp.now(),
    };
    
    if (currentSectionId !== undefined) {
      updateData.currentSectionId = currentSectionId;
    }
    if (completedSections !== undefined) {
      updateData.completedSections = completedSections;
    }
    if (progress !== undefined) {
      updateData.progress = progress;
    }
    
    await updateDoc(docRef, updateData);
    console.log(`Updated response data for lead ${leadId}`);
  } catch (error) {
    console.error(`Error updating response data for lead ${leadId}:`, error);
    throw error;
  }
};

/**
 * Mark questionnaire as complete/submitted
 */
export const submitQuestionnaireResponse = async (
  leadId: string
): Promise<void> => {
  try {
    const existing = await getQuestionnaireResponse(leadId);
    
    if (!existing) {
      console.warn(`No response found for lead ${leadId}`);
      return;
    }
    
    const docRef = doc(responsesCollection, existing.id);
    await updateDoc(docRef, {
      isComplete: true,
      submittedAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      progress: 100,
    });
    console.log(`Submitted response for lead ${leadId}`);
  } catch (error) {
    console.error(`Error submitting response for lead ${leadId}:`, error);
    throw error;
  }
};

// ============================================================================
// EXPORTS
// ============================================================================

export type { MergedQuestionnaireForm };
